/**
 * Constants for the join funnel (3-step pre-payment capture).
 */

import { POSITIONING_VARIANTS, POSITIONING_VARIANT_STORAGE_KEY, type PositioningVariant } from './payment';

export { POSITIONING_VARIANTS, POSITIONING_VARIANT_STORAGE_KEY };
export type { PositioningVariant };

/**
 * US states that don't have lab partners yet. Showing a soft warning when one
 * of these is selected on Step 3 — user can still join and upload their own
 * bloodwork.
 */
export const LAB_UNAVAILABLE_STATES = ['HI', 'RI'] as const;

/**
 * Hard floor: under-18 cannot proceed past Step 2 (Continue button disabled).
 */
export const MIN_AGE = 18;

/**
 * Soft warning threshold: Catch's risk models are tuned for adults under 85.
 * Continue is still allowed; just shows an inline notice.
 */
export const SOFT_AGE_WARNING_THRESHOLD = 85;

/**
 * Maximum age for which the funnel still treats DOB as plausible. Beyond this
 * we treat the input as a typo and show the same soft warning copy as ≥85.
 */
export const MAX_PLAUSIBLE_AGE = 120;

/**
 * localStorage key for the funnel session token (X-Funnel-Session header
 * value). Distinct from the legacy visitor-token used by member-backend.
 */
export const FUNNEL_SESSION_TOKEN_KEY = 'funnel-session-token';

/**
 * localStorage key for the funnel session UUID. Stamped onto Stripe
 * subscription metadata so catch-backend's webhook can link the funnel
 * session to the new member at `customer.subscription.created`. Opaque,
 * non-authenticating — the token (above) is what authorizes lead reads.
 */
export const FUNNEL_SESSION_ID_KEY = 'funnel-session-id';

/**
 * localStorage key for the full funnel lead (email, name, DOB, state, etc.)
 * as a JSON-serialized object. Written by `useFunnelLead.updateLead` after
 * every successful PATCH. Read synchronously by funnel step components in
 * their `useState` initializers — this is the local source of truth for
 * pre-filling fields. The server lead at /v1/intake/session/me/lead is a
 * durable mirror, not a UI hydration source.
 */
export const FUNNEL_LEAD_KEY = 'funnel-lead';

/**
 * localStorage key for the funnel session expiry timestamp (ISO 8601 UTC).
 * Written alongside the token at mint time so the client can rotate
 * proactively before the server's hard expiry kicks in. Avoids a class of
 * "user has a long-dead token in localStorage" 401s that historically only
 * surfaced when the user actually hit /me/lead with a stale token.
 */
export const FUNNEL_SESSION_EXPIRES_KEY = 'funnel-session-expires-at';

/**
 * Skew applied to FUNNEL_SESSION_EXPIRES_KEY before considering a token
 * stale. Re-minting 5 minutes before the server-side hard expiry leaves
 * margin for clock drift and in-flight requests.
 */
export const FUNNEL_SESSION_EXPIRY_SKEW_MS = 5 * 60 * 1000;
